import { useState } from "react";
import { toast } from "react-toastify";
import { useAdminOrganizationControllerSoftDelete } from "service";
import { AdminOrganizationResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "@/query-client";

export interface UseDeleteOrganization {
  pendingOrganization: AdminOrganizationResDto | null;
  isSubmitting: boolean;
  requestDelete: (organization: AdminOrganizationResDto) => void;
  confirmDelete: () => void;
  cancelDelete: () => void;
}

const useDeleteOrganization = (
  onDeleted?: () => void,
): UseDeleteOrganization => {
  const [pendingOrganization, setPendingOrganization] =
    useState<AdminOrganizationResDto | null>(null);

  const { mutate, isLoading } = useAdminOrganizationControllerSoftDelete({
    onSuccess: () => {
      toast.success("Organization Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/organizations",
          operationId: "adminOrganizationControllerFindMany",
          variables: {},
        }),
      );
      setPendingOrganization(null);
      onDeleted?.();
    },
    onError: () => {
      toast.error("Couldn't delete the organization. Please try again.");
    },
  });

  const requestDelete = (organization: AdminOrganizationResDto) => {
    setPendingOrganization(organization);
  };

  const cancelDelete = () => setPendingOrganization(null);

  const confirmDelete = () => {
    if (!pendingOrganization) return;
    mutate({ pathParams: { id: pendingOrganization.id } });
  };

  return {
    pendingOrganization,
    isSubmitting: isLoading,
    requestDelete,
    confirmDelete,
    cancelDelete,
  };
};

export default useDeleteOrganization;
